import React, { useState } from "react";
import {
  SolutionOutlined,
  KeyOutlined,
  UserOutlined,
  DownOutlined,
  AppstoreOutlined,
  FileSearchOutlined,
  PlayCircleOutlined,
  SettingOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { Layout, Menu, Dropdown, Button } from "antd";
import ViewsLayout from "./views/ViewsLayout";
import CredentialsLayout from "./credentials/CredentialsLayout";
import OverviewTasks from "./tasks/OverviewTasks";
import ViewTasks from "./tasks/ViewTasks";
import RunTasks from "./tasks/RunTasks";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectUser,
  selectUserRoles,
  update,
} from "../../features/login/loginSlice";
import { Cookies } from "react-cookie";
import { loading, notLoading } from "../../features/loading/loadingSlice";
import { fetchWithoutResponse } from "../../utils/EasyFetch";
import ChangePasswordModal from "./ChangePassword";
import SubscriptionsLayout from "./subscriptions/SubscriptionsLayout";

const { Header, Content, Footer, Sider } = Layout;

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("1");
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);

  const user = useAppSelector(selectUser);
  const roles = useAppSelector(selectUserRoles);
  const dispatch = useAppDispatch();

  const performLogout = () => {
    const cookies = new Cookies();
    const token: string = cookies.get("accessToken");
    dispatch(loading());
    fetchWithoutResponse("DELETE", "/auth", undefined, "Bearer " + token)
      .then(() => {
        dispatch(
          update({
            accessToken: undefined,
            refreshToken: undefined,
            user: undefined,
            roles: undefined,
          })
        );
        cookies.remove("accessToken");
        cookies.remove("user");
        cookies.remove("refreshToken");
        dispatch(notLoading());
      })
      .catch((err: any) => {
        dispatch(notLoading());
        console.log(err);
      });
  };

  const performChangePassword = async (password: string): Promise<boolean> => {
    const cookies = new Cookies();
    const userName = cookies.get("user");
    const token = cookies.get("accessToken");

    const body = {
      password,
    };

    dispatch(loading());
    return fetchWithoutResponse(
      "PATCH",
      `/credentials/${userName}`,
      body,
      `Bearer ${token}`
    )
      .then(() => {
        dispatch(notLoading());
        setIsChangePasswordModalOpen(false);
        return true;
      })
      .catch((err: any) => {
        dispatch(notLoading());
        console.log(err);
        return false;
      });
  };

  const showCreateModal = () => {
    setIsChangePasswordModalOpen(true);
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="changePassword" onClick={() => showCreateModal()}>
        Change Password
      </Menu.Item>
      <Menu.Item key="logout" onClick={() => performLogout()}>
        Logout
      </Menu.Item>
    </Menu>
  );

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const handleMenuSelect = ({ key }: { key: string }) => {
    setSelectedMenuItem(key);
  };

  interface MenuItem {
    label: string;
    icon: React.ReactNode;
    adminOnly: boolean;
    component: React.ReactNode;
    children?: Record<string, MenuItem>;
  }

  const menuItems: Record<string, MenuItem> = {
    "1": {
      label: "Views",
      icon: <SolutionOutlined />,
      adminOnly: false,
      component: <ViewsLayout />,
    },
    "2": {
      label: "Credentials",
      icon: <KeyOutlined />,
      adminOnly: true,
      component: <CredentialsLayout />,
    },
    "3": {
      label: "Tasks",
      icon: <SettingOutlined />,
      adminOnly: true,
      component: <ViewTasks />,
      children: {
        "3-1": {
          label: "Overview",
          icon: <AppstoreOutlined />,
          adminOnly: true,
          component: <OverviewTasks />,
        },
        "3-2": {
          label: "View Tasks",
          icon: <FileSearchOutlined />,
          adminOnly: true,
          component: <ViewTasks />,
        },
        "3-3": {
          label: "Run Tasks",
          icon: <PlayCircleOutlined />,
          adminOnly: true,
          component: <RunTasks />,
        },
      },
    },
    "4": {
      label: "Subscriptions",
      icon: <MessageOutlined />,
      adminOnly: true,
      component: <SubscriptionsLayout />,
    },
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider collapsible collapsed={collapsed} onCollapse={toggleCollapsed}>
        <div className="demo-logo-vertical" />
        <Menu
          theme="dark"
          defaultSelectedKeys={["1"]}
          mode="inline"
          onSelect={handleMenuSelect}
          selectedKeys={[selectedMenuItem]}
        >
          {Object.entries(menuItems).map(([key, item]) => {
            if (item.adminOnly && !(roles && roles.includes("ADMIN"))) {
              return null;
            }
            return item.children ? (
              <Menu.SubMenu key={key} icon={item.icon} title={item.label}>
                {Object.entries(item.children).map(([subKey, subItem]) => (
                  <Menu.Item key={subKey} icon={subItem.icon}>
                    {subItem.label}
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            ) : (
              <Menu.Item key={key} icon={item.icon}>
                {item.label}
              </Menu.Item>
            );
          })}
        </Menu>
      </Sider>
      <Layout>
        <Header
          style={{
            background: "#fff",
            padding: "0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="demo-logo-vertical" />
          <div style={{ display: "flex", alignItems: "center" }}>
            <Dropdown overlay={userMenu}>
              <Button
                onClick={(e) => e.preventDefault()}
                style={{
                  marginRight: "16px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <UserOutlined style={{ fontSize: 20, marginRight: 8 }} />
                {user} <DownOutlined />
              </Button>
            </Dropdown>
          </div>
        </Header>
        <ChangePasswordModal
          isVisible={isChangePasswordModalOpen}
          onCancel={() => setIsChangePasswordModalOpen(false)}
          changePassword={performChangePassword}
        />
        <Content style={{ margin: "0 16px" }}>
          {menuItems[selectedMenuItem]?.component ||
            menuItems["3"].children?.[selectedMenuItem]?.component || (
              <ViewsLayout />
            )}
        </Content>
        <Footer
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#fff",
          }}
        >
          <Button
            className="kofi-button"
            type="primary"
            icon={<img src="/kofi.svg" alt="Buy Me a Coffee at ko-fi.com" />}
            onClick={() =>
              window.open("https://ko-fi.com/Q5Q815ZBU4", "_blank")
            }
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "4px",
            }}
          >
            Fuel up the squad with sushi
          </Button>
          <p>2024 © All rights reserved.</p>
          <p>
            Made by <a href="https://kos.gg">kos.gg</a>
          </p>
        </Footer>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
